import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Field } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", email: "", phone: "", address: "", password: "" };

export const BorrowerDialog = ({ open, onOpenChange, borrower, onSaved }) => {
  const editing = !!borrower;
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({}); setFormError("");
    setF(borrower ? { ...EMPTY, ...borrower, password: "" } : EMPTY);
  }, [open, borrower]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!f.name.trim()) errs.name = "Name is required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) errs.email = "Enter a valid email address";
    if (!editing && f.password.length < 8) errs.password = "Temporary password must be at least 8 characters";
    setErrors(errs); setFormError("");
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const body = { name: f.name, email: f.email, phone: f.phone, address: f.address, password: f.password || null };
      const { data } = editing ? await api.put(`/borrowers/${borrower.id}`, body) : await api.post("/borrowers", body);
      toast.success(editing ? "Borrower updated" : `Borrower ${data.name} created`);
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      const { message, fields } = apiError(err);
      setErrors(fields); setFormError(message);
    } finally {
      setBusy(false);
    }
  };

  const inp = (k, props = {}) => (
    <Input id={k} value={f[k]} onChange={set(k)} className={`h-10 ${errors[k] ? "border-destructive" : ""}`} data-testid={`borrower-${k}-input`} {...props} />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="borrower-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Edit borrower" : "New borrower"}</DialogTitle>
          <DialogDescription>{editing ? "Update contact details. Leave password blank to keep it." : "The borrower signs in with this email and temporary password."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4" data-testid="borrower-form">
          <Field label="Full name" name="name" error={errors.name}>{inp("name", { autoFocus: true })}</Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Email" name="email" error={errors.email}>{inp("email", { type: "email" })}</Field>
            <Field label="Phone" name="phone" error={errors.phone}>{inp("phone", { type: "tel" })}</Field>
          </div>
          <Field label="Address" name="address" error={errors.address}>
            <Textarea id="address" rows={2} value={f.address} onChange={set("address")} data-testid="borrower-address-input" />
          </Field>
          <Field label={editing ? "Reset to temporary password (optional)" : "Temporary password"} name="password" error={errors.password} hint={editing ? "Borrower must change it on next login" : "At least 8 characters · borrower must change it on first login"}>
            {inp("password", { type: "text", autoComplete: "new-password" })}
          </Field>
          {formError && !errors.email && <p className="text-sm text-destructive" data-testid="borrower-form-error">{formError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="borrower-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="borrower-save-button">{busy && <Loader2 className="animate-spin" />}{editing ? "Save changes" : "Create borrower"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
